import { useEffect, useState } from 'react';
import { Card, Table, Badge, Alert, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import {
  faEdit,

  faCalendar,
  faClock,

  faUsers,

  faPoundSign,

  faUserMd
} from '@fortawesome/free-solid-svg-icons';

import Button from 'components/base/Button';

import { getMyShifts, toggleShiftStatus } from 'features/shift/shiftApi';


const PharmacistManageShiftsPage = () => {
  const navigate = useNavigate();

  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const fetchShifts = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getMyShifts();
      setShifts(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  const handleToggle = async (shift: any) => {
    try {
      setTogglingId(shift.id);

      await toggleShiftStatus(shift.id);

      // Optimistic update
      setShifts(prev =>
        prev.map(s =>
          s.id === shift.id
            ? { ...s, status: s.status === 'open' ? 'closed' : 'open' }
            : s
        )
      );
    } catch (err: any) {
      alert(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Manage Shifts</h3>

            <Button
              variant="phoenix-primary"
              onClick={() => {
                // navigate to create shift page
                navigate('/dashboard/pharmacy/create-shift');
              }}
            >
              + Create Shift
            </Button>
          </div>
          <Card>
            <Card.Body>
              <Table responsive hover className="align-middle">

                <thead>
                  <tr>
                    <th style={{ width: '120px' }}>Date</th>

                    <th style={{ width: '140px' }}>Time</th>

                    <th style={{ minWidth: '250px' }}>Location</th>

                    <th style={{ width: '100px' }}>Pay</th>

                    <th style={{ width: '170px' }}>Locum Role</th>

                    <th style={{ width: '110px' }}>Locums</th>

                    <th style={{ width: '120px' }}>Status</th>

                    <th style={{ width: '120px' }} className="text-center">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {shifts.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        No shifts found
                      </td>
                    </tr>
                  ) : (
                    shifts.map(shift => (
                      <tr key={shift.id}>
                        <td>
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="me-2 text-primary"
                          />
                          {new Date(shift.date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit'
                          })}
                        </td>

                        <td>
                          <FontAwesomeIcon
                            icon={faClock}
                            className="me-2 text-primary"
                          />
                          {shift.start_time.slice(0, 5)} -{' '}
                          {shift.end_time.slice(0, 5)}
                        </td>

                        <td>
                          <span>
                            {shift.location}
                            <br></br>
                          </span>
                          {shift.address && (
                            <small className="text-muted">
                              {shift.address}
                            </small>
                          )}
                        </td>

                        <td>
                          <FontAwesomeIcon
                            icon={faPoundSign}
                            className="me-2 text-success"
                          />
                          {shift.pay_rate}
                        </td>

                        <td>
                          <FontAwesomeIcon
                            icon={faUserMd}
                            className="me-2 text-success"
                          />
                          {shift.locum_role}
                        </td>

                        <td>
                          <FontAwesomeIcon
                            icon={faUsers}
                            className="me-2 text-info"
                          />
                          {shift.booked_locums_count} / {shift.required_locums}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Badge
                              bg={
                                shift.status === 'open'
                                  ? 'success'
                                  : 'secondary'
                              }
                            >
                              {shift.status}
                            </Badge>
                            <Form.Check
                              type="switch"
                              id={`shift-switch-${shift.id}`}
                              checked={shift.status === 'open'}
                              disabled={togglingId === shift.id}
                              onChange={() => handleToggle(shift)}
                            />
                          </div>
                        </td>

                        <td>
                          <Button
                            size="sm"
                            variant="phoenix-primary"
                            startIcon={<FontAwesomeIcon icon={faEdit} />}
                            onClick={() => {
                              navigate(
                                `/dashboard/pharmacy/create-shift?id=${shift.id}`
                              );
                            }}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
};

export default PharmacistManageShiftsPage;
